import { useEffect, useMemo, useState } from 'react';
import { useMetadata } from '@contexts/org-metadata';
import MarketPlaceService, {
  processMarketplaceData,
} from '@utility/services/marketplace';
import { useDebouncedEffect } from './debounceAsync';
import type {
  IMarketplaceDropdownOption,
  IMarketplaceMeta,
  IShippingSettings,
  IMarketplaceSync,
  IUseMarketPlaceHookReturn,
  IUseMarketplaceDropdownHookReturn,
  IUseMarketplaceShippingReturn,
  IUseMarketplaceSyncHookReturn,
  IUseMarketplaceAutoSyncHookReturn,
  IConnectedMarketplace,
} from 'types/hook/marketplace';

const getErrorMessage = (err: unknown): string =>
  err instanceof Error ? err.message : String(err);

export const useMarketPlaceHook = (): IUseMarketPlaceHookReturn => {
  const {
    loading,
    marketplaces: marketplaceMetaData,
    refreshMetadata,
  } = useMetadata() as {
    loading: boolean;
    marketplaces: IMarketplaceMeta[];
    refreshMetadata: () => void | Promise<void>;
  };

  const [connectedMarketPlaces, setConnectedMarketPlaces] = useState<
    IConnectedMarketplace[]
  >([]);
  const [tobeAddMarketPlaces, setTobeAddMarketPlaces] = useState<
    IMarketplaceMeta[]
  >([]);
  const [marketplaces, setMarketPlaces] = useState<IMarketplaceMeta[]>([]);
  const [uniqueConnectedMP, setUniqueConnectedMP] = useState<
    IConnectedMarketplace[]
  >([]);
  const [continents, setContinents] = useState<string[]>([]);
  const [error, setError] = useState<string>('');
  const [seedMarketplace, setSeedMarketplace] =
    useState<IMarketplaceMeta | null>(null);

  const fetchData = async (): Promise<void> => {
    try {
      setError('');
      const processed = processMarketplaceData(marketplaceMetaData);

      setSeedMarketplace(processed.seedMarketplace);
      setContinents(processed.continents);
      setConnectedMarketPlaces(processed.connectedMarketPlaces);
      setTobeAddMarketPlaces(processed.tobeAddMarketPlaces);
      setMarketPlaces(processed.marketplaces);
      setUniqueConnectedMP(processed.uniqueConnectedMP);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  useEffect(() => {
    fetchData();
  }, [marketplaceMetaData]);

  return {
    connectedMarketPlaces,
    tobeAddMarketPlaces,
    loading,
    error,
    setConnectedMarketPlaces,
    marketplaces,
    uniqueConnectedMP,
    refreshMetadata,
    continents,
    seedMarketplace,
  };
};

export const useMarketPlaceDropdownHook = (
  aliasAsValue: boolean,
): IUseMarketplaceDropdownHookReturn => {
  const [data, setData] = useState<IMarketplaceDropdownOption[]>([]);
  const { loading, marketplaces } = useMetadata() as {
    loading: boolean;
    marketplaces: IMarketplaceMeta[];
  };
  const [error, setError] = useState<string>('');

  const fetchData = async (): Promise<void> => {
    try {
      setError('');

      const sortedData = [...(marketplaces || [])].sort((a, b) =>
        (a.alias || '').localeCompare(b.alias || ''),
      );

      const temp: IMarketplaceDropdownOption[] = sortedData.map(item => ({
        text: item.alias,
        value: aliasAsValue ? item.alias : item._id,
        flag: (item.country_code || '').toLowerCase(),
        item,
        currency_code: item.currency_code,
      }));

      setData(temp);
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  useEffect(() => {
    fetchData();
  }, [marketplaces, aliasAsValue]);

  return { data, loading, error };
};

export const useMarketplaceShipping = (
  regionId: string | undefined,
): IUseMarketplaceShippingReturn => {
  const [data, setData] = useState<IShippingSettings | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const fetchData = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      const result = (await MarketPlaceService.getShippingSettings(
        regionId,
      )) as IShippingSettings;
      setData(result);
    } catch (err) {
      setData(null);
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (regionId) void fetchData();
  }, [regionId]);

  return { data, loading, error };
};

export const useMarketplaceSyncHook = (
  marketplaceId: string | undefined,
  doSync: boolean = true,
): IUseMarketplaceSyncHookReturn => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<IMarketplaceSync | null>(null);
  const [error, setError] = useState<string>('');
  const [refresh, setRefresh] = useState<number>(0);

  const refreshSidebar = (): void => {
    setRefresh(r => r + 1);
  };

  const fetchMarketplaceSync = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      const result = (await MarketPlaceService.getMarketplaceSync({
        marketplace_id: marketplaceId,
      })) as { data: IMarketplaceSync };
      setData(result?.data ?? null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (doSync && marketplaceId) void fetchMarketplaceSync();
  }, [marketplaceId, refresh, doSync]);

  return {
    isLoading,
    data,
    error,
    refreshSidebar,
  };
};

export const useMarketplaceAutoSyncHook = (
  marketplaceId: string | undefined,
): IUseMarketplaceAutoSyncHookReturn => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [data, setData] = useState<IMarketplaceSync | null>(null);
  const [error, setError] = useState<string>('');
  const [refresh, setRefresh] = useState<number>(0);

  const { progress, estimated_time } = useMemo(() => {
    const catalogPct = data?.catalog?.percentage ?? 0;
    const ordersPct = data?.orders?.percentage ?? 0;

    if (catalogPct !== 100) {
      return { progress: Math.trunc(catalogPct), estimated_time: 0 };
    }
    if (ordersPct !== 100) {
      return {
        progress: Math.trunc(ordersPct),
        estimated_time: data?.orders?.estimated_time ?? 0,
      };
    }
    const done = ordersPct === 100 && catalogPct === 100 ? 100 : 0;
    return { progress: done, estimated_time: 0 };
  }, [data]);

  const refreshData = (): void => setRefresh(r => r + 1);
  const refreshSidebar = refreshData;

  const fetchMarketplaceSync = async (): Promise<void> => {
    try {
      setLoading(true);
      setError('');
      const result = (await MarketPlaceService.getMarketplaceSync({
        marketplace_id: marketplaceId,
      })) as { data: IMarketplaceSync };
      setData(result?.data ?? null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let timer: ReturnType<typeof setTimeout> | undefined;
    if (data?.status === 'in_progress') {
      timer = setTimeout(() => {
        refreshData();
      }, 500);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [data]);

  useEffect(() => {
    if (marketplaceId) void fetchMarketplaceSync();
  }, [marketplaceId, refresh]);

  return {
    isLoading,
    data,
    error,
    progress,
    estimated_time,
    refreshSidebar,
  };
};
